import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';

const CLEAR_ALL_VALUE = '#clear-all';

const useStyles = makeStyles(() => ({
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
    },
    chip: {
        margin: 2,
        height: 24,
    },
    clearAllItem: {
        fontWeight: 500,
        color: '#666',
    },
    menuPaper: {
        maxHeight: '80vh',
    },
    menuList: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        columnGap: '8px',
    },
}));

export default function MultiSelectMenu({
    id,
    label,
    enums,
    className,
    rootClassName,
    wrapperClassName,
    onChange,
    value,
    ...otherProps
}) {
    const classes = useStyles();
    
    // Convert value to array if it's not already
    const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
    
    const menuItems = [
        // Add "Clear All" option at the top
        <MenuItem value={CLEAR_ALL_VALUE} key={CLEAR_ALL_VALUE} className={classes.clearAllItem}>
            <Checkbox checked={selectedValues.length === 0} />
            <ListItemText primary="全部" />
        </MenuItem>,
        ...Object.entries(enums).map(([enumId, enumValue]) => (
            <MenuItem value={enumId} key={enumId}>
                <Checkbox checked={selectedValues.indexOf(enumId) > -1} />
                <ListItemText primary={enumValue.displayName} />
            </MenuItem>
        ))
    ];
    
    const onChangeInternal = useCallback(e => {
        const newValue = e.target.value;
        // Convert to array if it's a string (shouldn't happen with multiple, but just in case)
        const arrayValue = Array.isArray(newValue) ? newValue : [newValue];
        
        // Check if "Clear All" was clicked
        if (arrayValue.includes(CLEAR_ALL_VALUE)) {
            // Clear all selections
            e.target.value = null;
        } else {
            // Filter out the CLEAR_ALL_VALUE and empty values
            const filteredValue = arrayValue.filter(v => v && v !== CLEAR_ALL_VALUE);
            // Convert empty array to null
            e.target.value = filteredValue.length > 0 ? filteredValue : null;
        }
        onChange(e);
    }, [onChange]);

    const renderValue = useCallback((selected) => {
        if (!selected || selected.length === 0) {
            return '全部';
        }
        return (
            <Box className={classes.chips}>
                {selected.map((value) => (
                    <Chip
                        key={value}
                        label={enums[value]?.displayName || value}
                        className={classes.chip}
                        size="small"
                    />
                ))}
            </Box>
        );
    }, [enums, classes.chips, classes.chip]);

    return (
        <FormControl
            variant='outlined'
            fullWidth
            className={wrapperClassName}
        >
            {label && (
                <InputLabel id={`${id}-label`}>
                    {label}
                </InputLabel>
            )}
            <Select
                labelId={label && `${id}-label`}
                className={className}
                id={id}
                label={label}
                multiple
                displayEmpty
                value={selectedValues}
                onChange={onChangeInternal}
                renderValue={renderValue}
                classes={{
                    root: rootClassName,
                }}
                MenuProps={{
                    PaperProps: {
                        className: classes.menuPaper,
                    },
                    MenuListProps: {
                        className: classes.menuList,
                    },
                    anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    },
                    transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                }}
                {...otherProps}
            >
                {menuItems}
            </Select>
        </FormControl>
    );
}

MultiSelectMenu.propTypes = {
    id: PropTypes.string.isRequired,
    wrapperClassName: PropTypes.string,
    className: PropTypes.string,
    rootClassName: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string)
    ]),
    onChange: PropTypes.func.isRequired,
    enums: PropTypes.objectOf(
        PropTypes.shape({
            displayName: PropTypes.string
        })
    ).isRequired,
};
